/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable sort-keys */
import {
   ChangeDetectionStrategy,
   ChangeDetectorRef,
   Component,
   EventEmitter,
   Input,
   OnDestroy,
   Output,
   ViewEncapsulation,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { CallState } from '@lcs/shared/api';
import { Actions, ofType } from '@ngrx/effects';
import { Observable, Subject, of } from 'rxjs';
import { filter, switchMap, takeUntil } from 'rxjs/operators';
import { ConfirmationDialogService } from 'src/app/common-components/confirmation-dialog/confirmation-dialog.service';
import { DialogSize } from 'src/app/common-components/confirmation-dialog/enumerations/dialog-size.enum';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { convertDayOfWeekToNumber } from 'src/app/common-components/helpers/schedule-helper';
import { scheduleHourItemDialogSubmit } from 'src/app/pages/schedule-details/state/schedule-detail.actions';
import { AddScheduleHourComponent } from '../add-schedule-hour/containers/add-schedule-hour.component';
import { ScheduleHourItemView } from './models/schedule-hours.model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-schedule-hours',
    styleUrls: ['./schedule-hours.component.scss'],
    templateUrl: './schedule-hours.component.html',
    standalone: false
})
export class ScheduleHoursComponent implements ControlValueAccessor, OnDestroy {
   @Input() callState: CallState;

   @Input() canEdit: boolean;

   @Input() canDelete: boolean;

   @Output() addScheduleHourClick = new EventEmitter<void>();

   @Output() editScheduleHourClick = new EventEmitter<number>();

   columns: string[] = ['DayOfWeek', 'OpenTime', 'CloseTime', 'MoreActions'];

   scheduleHourItems: ScheduleHourItemView[] = [];

   disabled = false;

   onTouched: () => void;

   private destroyed$ = new Subject<void>();

   private onChange: (value: Array<ScheduleHourItemView> | null) => void;

   constructor(
      private changeDetectorRef: ChangeDetectorRef,
      control: NgControl,
      private actions$: Actions,
      private confirmationService: ConfirmationDialogService,
      private dialogService: LcsDialogService
   ) {
      control.valueAccessor = this;
      this.actions$.pipe(ofType(scheduleHourItemDialogSubmit), takeUntil(this.destroyed$)).subscribe((action) => {
         if (action.scheduleHourItem.selectedDays === null) {
            const scheduleHourToAdd: ScheduleHourItemView = {
               closeTime:
                  action.scheduleHourItem.closeTime !== null
                     ? this.convertTimeStringToDate(action.scheduleHourItem.closeTime)
                     : null,
               dayOfWeek: action.scheduleHourItem.dayOfWeek,
               isDeleted: false,
               openTime:
                  action.scheduleHourItem.openTime !== null
                     ? this.convertTimeStringToDate(action.scheduleHourItem.openTime)
                     : null,
               scheduleDetailId: 0,
            };
            this.scheduleHourItems.push(scheduleHourToAdd);
         } else {
            action.scheduleHourItem.selectedDays.forEach(
               function (day) {
                  const scheduleHourToAdd: ScheduleHourItemView = {
                     closeTime:
                        action.scheduleHourItem.closeTime !== null
                           ? this.convertTimeStringToDate(action.scheduleHourItem.closeTime)
                           : null,
                     dayOfWeek: day,
                     isDeleted: false,
                     openTime:
                        action.scheduleHourItem.openTime !== null
                           ? this.convertTimeStringToDate(action.scheduleHourItem.openTime)
                           : null,
                     scheduleDetailId: 0,
                  };
                  this.scheduleHourItems.push(scheduleHourToAdd);
               }.bind(this)
            );
            this.scheduleHourItems = [...this.scheduleHourItems];
         }
         this.onChange(this.scheduleHourItems);
         this.changeDetectorRef.markForCheck();
      });
   }

   deleteConfirmationDialog(): Observable<boolean> {
      const message = 'Changes will not take effect until you save the form.';

      const dialogRef = this.confirmationService.confirm({
         data: {
            header: 'Delete this Schedule Hour?',
            message,
         },
         size: DialogSize.Medium,
      });

      return dialogRef.afterClosed().pipe(
         takeUntil(this.destroyed$),
         switchMap((confirmed) => {
            if (confirmed) {
               return of(true);
            } else {
               return of(false);
            }
         })
      );
   }

   onDeleteClick(index: number): void {
      this.deleteConfirmationDialog()
         .pipe(takeUntil(this.destroyed$))
         .subscribe((confirmed) => {
            if (confirmed) {
               this.scheduleHourItems[index].isDeleted = true;
               this.changeDetectorRef.markForCheck();
               this.onChange(this.scheduleHourItems);
            }
         });
   }

   writeValue(scheduleHourItems: ScheduleHourItemView[]): void {
      if (scheduleHourItems?.length > 0) {
         this.scheduleHourItems = scheduleHourItems
            .map((item) => {
               return { ...item, isDeleted: false };
            })
            .sort((a, b) => {
               const dayA = convertDayOfWeekToNumber(a.dayOfWeek || '') || 0;
               const dayB = convertDayOfWeekToNumber(b.dayOfWeek || '') || 0;

               if (dayA === dayB) {
                  const aTime = a.openTime instanceof Date ? a.openTime.getTime() : -Infinity;
                  const bTime = b.openTime instanceof Date ? b.openTime.getTime() : -Infinity;
                  return aTime - bTime;
               }
               return dayA - dayB;
            });
      } else {
         this.scheduleHourItems = [];
      }
      this.changeDetectorRef.markForCheck();
   }

   registerOnChange(onChange: (value: Array<ScheduleHourItemView> | null) => void): void {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   setDisabledState?(isDisabled: boolean): void {
      this.disabled = isDisabled;
      this.changeDetectorRef.markForCheck();
   }

   onEditScheduleHourClick(index: number): void {
      const dialogRef = this.dialogService.openFormComponent(AddScheduleHourComponent, {
         closeOnNavigation: true,
         data: {
            scheduleHourFormData: this.scheduleHourItems[index],
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      dialogRef
         .afterClosed()
         .pipe(
            filter((scheduleHourItem) => {
               return scheduleHourItem !== undefined;
            })
         )
         .subscribe((scheduleHourItem) => {
            (scheduleHourItem.openTime = scheduleHourItem.openTime
               ? this.convertTimeStringToDate(scheduleHourItem.openTime)
               : null),
               (scheduleHourItem.closeTime = scheduleHourItem.closeTime
                  ? this.convertTimeStringToDate(scheduleHourItem.closeTime)
                  : null),
               (scheduleHourItem.dayOfWeek = scheduleHourItem.dayOfWeek?.dayOfWeek),
               (scheduleHourItem['isDeleted'] = false);
            this.scheduleHourItems[index] = scheduleHourItem;
            this.onChange(this.scheduleHourItems);
            this.changeDetectorRef.markForCheck();
         });
   }

   onAddScheduleHourClick() {
      this.addScheduleHourClick.emit();
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   private convertTimeStringToDate(timeString: string): Date {
      const [hours, minutes] = timeString.split(':').map(Number);
      const date = new Date(Date.UTC(1970, 0, 1, hours, minutes, 0));
      return date;
   }
}
